import React, { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import Input from "components/ui/Input";
import { useGenerateOrderRegisterReport } from "http-hooks/agorder/useAgorder";
import { useGenerateSalebillRegisterReport, useGenerateSalebillRegisterReportFull, useGenerateSalebillRegisterCSV, useGenerateSalebillRegisterCSVFull } from "http-hooks/salebill/useSalebill";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

// Report format options
const reportFormatOptions = [
  { value: "csv", label: "CSV" },
  { value: "print", label: "Print" }
];

// With group options
const withGroupOptions = [
  { value: "N", label: "No" },
  { value: "Y", label: "Yes" }
];

// With discounts options
const withDiscountsOptions = [
  { value: "0", label: "No" },
  { value: "1", label: "Yes" }
];

const RegisterGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportOf = searchParams.get("reportof"); // G, S, P
  const formTp = searchParams.get("formtp"); // ORD, BILL, VCN, VDN, DIVERT

  // Determine if this is a sale bill register (not order register)
  const isSaleBillRegister = formTp !== "ORD";
  
  // Mutations for generating reports
  const { mutateAsync: generateOrderReport, isPending: isGeneratingOrder } = useGenerateOrderRegisterReport();
  const { mutateAsync: generateSaleBillReport, isPending: isGeneratingSaleBill } = useGenerateSalebillRegisterReport();
  const { mutateAsync: generateSaleBillReportFull, isPending: isGeneratingSaleBillFull } = useGenerateSalebillRegisterReportFull();
  const { mutateAsync: generateSaleBillCSV, isPending: isGeneratingCSV } = useGenerateSalebillRegisterCSV();
  const { mutateAsync: generateSaleBillCSVFull, isPending: isGeneratingCSVFull } = useGenerateSalebillRegisterCSVFull();
  
  // Determine which loading state to use
  const isGenerating = isGeneratingOrder || isGeneratingSaleBill || isGeneratingSaleBillFull || isGeneratingCSV || isGeneratingCSVFull;

  // State
  const [reportFormat, setReportFormat] = useState(null);
  const [withDiscounts, setWithDiscounts] = useState({ value: "0", label: "No" });
  const [reportType, setReportType] = useState(null);
  const [withGroup, setWithGroup] = useState({ value: "N", label: "No" });
  const [selectedVendors, setSelectedVendors] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Determine heading based on formtp and reportof
  const heading = useMemo(() => {
    switch (formTp) {
      case "ORD":
        return "Order Register";
      case "VCN":
        return "Credit Note Register";
      case "VDN":
        return "Debit Note Register";
      case "BILL":
        if (reportOf === "P") return "Purchase Bill Register";
        if (reportOf === "S" || reportOf === "G") return "Salebill Register";
        return "Bill Register";
      case "DIVERT":
        return "Diverted Bills Register";
      default:
        return "Register";
    }
  }, [formTp, reportOf]);

  // Determine available report types based on reportOf
  const reportTypeOptions = useMemo(() => {
    const options = [{ value: "date", label: "Date Range" }];
    
    // Add customer option if applicable
    if (reportOf !== "P") {
      options.push({ value: "customer", label: "Customer" });
    }
    
    // Add vendor option if applicable
    if (reportOf !== "S") {
      options.push({ value: "vendor", label: "Vendor" });
    }
    
    return options;
  }, [reportOf]);

  // Show report format for non-DIVERT and non-ORD forms
  const showFormat = formTp !== "DIVERT" && formTp !== "ORD";
  
  // Hide discounts field (keeping functionality but not showing UI)
  const showDiscounts = false;

  const handleReportTypeChange = (selected) => {
    setReportType(selected);
    // Reset selections when report type changes
    setSelectedVendors(null);
    setSelectedCustomers(null);
    setWithGroup({ value: "N", label: "No" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Extract IDs - handle both single object and array
    const vendorIds = Array.isArray(selectedVendors) 
      ? selectedVendors.map(v => v.ps_Id) 
      : selectedVendors?.ps_Id;
    
    const customerIds = Array.isArray(selectedCustomers) 
      ? selectedCustomers.map(c => c.ps_Id) 
      : selectedCustomers?.ps_Id;

    const formData = {
      reportOf,
      formTp,
      reportFormat: reportFormat?.value,
      withDiscounts: withDiscounts?.value,
      reportType: reportType?.value,
      withGroup: withGroup?.value,
      vendors: vendorIds,
      customers: customerIds,
      fromDate,
      toDate
    };

    // Determine which report generator to use based on format and type
    let data;
    
    // CSV format (only for sale bill register)
    if (isSaleBillRegister && reportFormat?.value === "csv") {
      // Check if with discounts
      if (withDiscounts?.value === "1") {
        // Use CSV export with discounts - this handles download automatically
        await generateSaleBillCSVFull(formData);
      } else {
        // Use regular CSV export - this handles download automatically
        await generateSaleBillCSV(formData);
      }
      return; // No need to open anything, file is downloaded
    }
    
    // Print/HTML format
    if (isSaleBillRegister && withDiscounts?.value === "1") {
      // Use full version with discounts
      data = await generateSaleBillReportFull(formData);
    } else if (isSaleBillRegister) {
      // Use regular sale bill register
      data = await generateSaleBillReport(formData);
    } else {
      // Use order register
      data = await generateOrderReport(formData);
    }
    
    // Navigate to Document Viewer
    if (data?.htmlUrl) {
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);
      
      const route = getDocumentViewerRoute('register', {
        htmlUrl,
        pdfUrl,
        documentNumber: heading,
        documentType: reportType?.label + ' Report'
      });
      
      navigate(route);
    }
  };

  const isFormValid = () => {
    if (!reportType) return false;

    // Check format for forms that require it
    if (showFormat && !reportFormat) return false;
    
    if (reportType.value === "date") {
      return fromDate && toDate;
    } else if (reportType.value === "customer") {
      // Customerwise: need 1 customer (single)
      return selectedCustomers !== null && !Array.isArray(selectedCustomers);
    } else if (reportType.value === "vendor") {
      // Vendorwise: need 1 vendor (single)
      return selectedVendors !== null && !Array.isArray(selectedVendors);
    }
    
    return false;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            {heading}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate {heading.toLowerCase()} reports
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              
              {/* Report Format - only show for certain forms */}
              {showFormat && (
                <div className="sm:col-span-3">
                  <Select
                    value={reportFormat}
                    onChange={setReportFormat}
                    options={reportFormatOptions}
                    label="Report Format"
                    placeholder="Choose Here"
                    isRequired={true}
                    isClearable={false}
                  />
                </div>
              )}

              {/* With Discounts - hidden but functionality kept */}
              {showDiscounts && (
                <div className="sm:col-span-3">
                  <Select
                    value={withDiscounts}
                    onChange={setWithDiscounts}
                    options={withDiscountsOptions}
                    label="Discounts"
                    placeholder="Choose Here"
                    isRequired={true}
                    isClearable={false}
                  />
                </div>
              )}

              {/* Report Type */}
              <div className="sm:col-span-3">
                <Select
                  value={reportType}
                  onChange={handleReportTypeChange}
                  options={reportTypeOptions}
                  label="Report Type"
                  placeholder="Choose Here"
                  isRequired={true}
                  isClearable={false}
                />
              </div>

              {/* Conditional Fields based on Report Type */}
              {reportType?.value === "date" && (
                <>
                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      label="From Date"
                      isRequired={true}
                      fys={true}
                      withinFY={true}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      label="To Date"
                      isRequired={true}
                      fye={true}
                      withinFY={true}
                    />
                  </div>
                </>
              )}

              {reportType?.value === "customer" && (
                <>
                  <div className="sm:col-span-3">
                    <Select
                      value={withGroup}
                      onChange={setWithGroup}
                      options={withGroupOptions}
                      label="With Group"
                      placeholder="Choose Here"
                      isRequired={true}
                      isClearable={false}
                    />
                  </div>
                  
                  <div className="sm:col-span-3">
                    <SelectCustomers
                      value={selectedCustomers}
                      onChange={setSelectedCustomers}
                      label="Customer"
                      placeholder="Choose Here"
                      isRequired={true}
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <SelectVendors
                      value={selectedVendors}
                      onChange={setSelectedVendors}
                      label="Vendor (Optional)"
                      placeholder="Choose Vendors"
                      isMulti={true}
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      label="From Date (Optional)"
                      fys={true}
                      withinFY={true}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      label="To Date (Optional)"
                      fye={true}
                      withinFY={true}
                    />
                  </div>
                </>
              )}

              {reportType?.value === "vendor" && (
                <>
                  <div className="sm:col-span-3">
                    <Select
                      value={withGroup}
                      onChange={setWithGroup}
                      options={withGroupOptions}
                      label="With Group"
                      placeholder="Choose Here"
                      isRequired={true}
                      isClearable={false}
                    />
                  </div>
                  
                  <div className="sm:col-span-3">
                    <SelectVendors
                      value={selectedVendors}
                      onChange={setSelectedVendors}
                      label="Vendor"
                      placeholder="Choose Here"
                      isRequired={true}
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <SelectCustomers
                      value={selectedCustomers}
                      onChange={setSelectedCustomers}
                      label="Customer (Optional)"
                      placeholder="Choose Customers"
                      isMulti={true}
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      label="From Date (Optional)"
                      fys={true}
                      withinFY={true}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      label="To Date (Optional)"
                      fye={true}
                      withinFY={true}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              disabled={!isFormValid() || isGenerating}
              className={`rounded-md px-8 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                isFormValid() && !isGenerating
                  ? "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isGenerating ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterGenerator;

